import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { EligibleMedicareCoverage } from 'src/app/models/eligible/eligible_medicare_coverage.model';
import { InsuranceCoverage, Patient } from 'src/app/models/patient.model';
import {
  BenefitsBadge,
  benefitsBadge,
  benefitsTooltip,
} from 'src/app/clinical/dashboards/patient-enrollments/benefits-check-display';
import { ManualBenefitsCheckSummary } from 'src/app/shared/benefits-check/manual-benefits-check.model';
import { ManualBenefitsCheckService } from 'src/app/shared/benefits-check/manual-benefits-check.service';
import { DialogService } from 'src/app/services/dialog.service';
import { PatientDetailService } from 'src/app/services/patient-detail.service';
import { PatientService } from 'src/app/services/patient.service';
import { SnackService } from 'src/app/services/snack.service';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-eligibility-profile',
  templateUrl: './eligibility-profile.component.html',
  styleUrls: ['./eligibility-profile.component.scss'],
})
export class EligibilityProfileComponent implements OnInit, OnDestroy {
  coverageColumns = ['member_id', 'payor', 'type', 'groupId', 'actions'];
  displayedColumns = ['number', 'type', 'remove'];
  eligibility: EligibleMedicareCoverage;
  isLoading = false;
  /** Latest manual benefits check on this chart; updated in place from the response. */
  benefitsCheck: ManualBenefitsCheckSummary | null = null;
  checkingBenefits = false;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    public dialogService: DialogService,
    public patientService: PatientService,
    public patientDetailService: PatientDetailService,
    private datePipe: DatePipe,
    private db: AngularFirestore,
    private snackService: SnackService,
    private manualBenefitsCheck: ManualBenefitsCheckService
  ) {}

  /** Shown only when the backend says the feature is on and this user is allowed (fails closed). */
  get canRunBenefitsCheck$(): Observable<boolean> {
    return this.manualBenefitsCheck.canRun$;
  }

  get benefitsBadge(): BenefitsBadge | null {
    return benefitsBadge(this.benefitsCheck);
  }

  get benefitsTooltip(): string {
    return benefitsTooltip(this.benefitsCheck);
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  runBenefitsCheck(patient: Patient): void {
    const patientId = patient?.user_id;
    if (this.checkingBenefits || !patientId) {
      return;
    }
    this.checkingBenefits = true;
    this.manualBenefitsCheck
      .runAndShow(patientId)
      .pipe(
        finalize(() => (this.checkingBenefits = false)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((check) => {
        if (check) {
          this.benefitsCheck = check;
        }
      });
  }

  checkMedicareEligbility(coverage: InsuranceCoverage, patient: Patient) {
    patient.dob =
      typeof patient.dob === 'string' ? new Date(patient.dob) : patient.dob.toDate ? patient.dob.toDate() : patient.dob;
    const body = {
      provider_npi: '1234567893',
      member_id: coverage.member_id,
      member_first_name: patient.firstName,
      member_last_name: patient.lastName,
      member_dob: patient?.dob ? this.datePipe.transform(patient?.dob, 'yyyy-MM-dd') : '2020-01-01',
    };

    this.isLoading = true;
    // this.cdr.detectChanges();
    const url = 'https://us-central1-welby-dev.cloudfunctions.net/eligibleRest/eligible_coverage';
    this.http.post(url, body).subscribe(
      (val) => {
        this.eligibility = val;
        console.log('POST call successful value returned in body', val);
      },
      (response) => {
        console.log('Post request to eligible returned false', response);
      },
      () => {
        this.isLoading = false;
      }
    );
  }

  async deleteCoverage(coverage: InsuranceCoverage) {
    try {
      const uid = this.patientService.currentPatientServiceID;
      await this.db.collection('users').doc(uid).collection('my_health_coverage').doc(coverage.id).delete();
      this.snackService.genericSnackBar('Coverage deleted', ['success-snackbar'], 5000);
    } catch (error) {
      console.error(error);
      this.snackService.genericSnackBar('Error deleting coverage', ['error-snackbar'], 5000);
    }
  }
}
